import { useState, useEffect } from "react";
import { api } from "../../context";

export default function Analytics({ t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const CARD=t.card,BORDER=t.border,TEXT=t.text,SUB=t.sub,PRI=t.pri,GRN=t.grn,RED=t.red,GOLD=t.gold;
  const fmt = (n) => Number(n || 0).toLocaleString();

  useEffect(() => {
    api("/admin/analytics").then(r => {
      if (r.ok) setData(r.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div style={{ padding: 40, color: SUB, textAlign: "center" }}>⏳ Loading analytics...</div>;

  const MiniBar = ({ items = [], color, height = 80, valueKey = "count" }) => {
    const max = Math.max(...items.map(d => d[valueKey] || 0), 1);
    return (
      <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height }}>
        {items.map((d, i) => {
          const h = Math.max(2, ((d[valueKey] || 0) / max) * (height - 4));
          return <div key={i} style={{ flex: 1, height: h, background: color, borderRadius: "2px 2px 0 0", opacity: 0.85, minWidth: 3 }} title={`${d.date}: ${d[valueKey]}`} />;
        })}
      </div>
    );
  };

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: TEXT, marginBottom: 4 }}>📈 Analytics</div>
      <div style={{ fontSize: 12, color: SUB, marginBottom: 24 }}>Full platform analytics — signups, activity, messages and more</div>

      {/* Key metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Total Users",    value: fmt(data?.total_users),    color: PRI },
          { label: "Total Posts",    value: fmt(data?.total_posts),    color: "#8b5cf6" },
          { label: "Total Messages", value: fmt(data?.total_messages), color: "#06b6d4" },
        ].map((s, i) => (
          <div key={i} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "20px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 32, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: SUB, marginTop: 6, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* 3 charts row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
        {[
          { label: "Daily Signups (30 days)", data: data?.signups, color: PRI },
          { label: "Daily Active Users (30 days)", data: data?.active, color: GRN },
          { label: "Daily Messages (30 days)", data: data?.messages, color: "#06b6d4" },
        ].map((chart, i) => (
          <div key={i} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 16 }}>{chart.label}</div>
            {chart.data?.length > 0
              ? <>
                  <MiniBar items={chart.data} color={chart.color} height={80} />
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                    <span style={{ fontSize: 10, color: SUB }}>{chart.data[0]?.date?.slice(5)}</span>
                    <span style={{ fontSize: 10, color: SUB }}>{chart.data[chart.data.length - 1]?.date?.slice(5)}</span>
                  </div>
                </>
              : <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: SUB, fontSize: 12 }}>No data yet</div>
            }
          </div>
        ))}
      </div>

      {/* Retention */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>🔄 7-Day Retention</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 13, color: SUB }}>Users who came back</span>
            <span style={{ fontSize: 28, fontWeight: 800, color: data?.retention >= 50 ? GRN : data?.retention >= 25 ? GOLD : RED }}>{data?.retention || 0}%</span>
          </div>
          <div style={{ height: 10, borderRadius: 5, background: BORDER }}>
            <div style={{ height: 10, borderRadius: 5, background: data?.retention >= 50 ? GRN : data?.retention >= 25 ? GOLD : RED, width: `${data?.retention || 0}%`, transition: "width 1s" }} />
          </div>
          <div style={{ fontSize: 12, color: SUB, marginTop: 8 }}>
            {(data?.retention || 0) >= 50 ? "✅ Excellent retention" : (data?.retention || 0) >= 25 ? "⚠️ Needs improvement" : "❌ Critical — users not returning"}
          </div>
        </div>

        {/* Plan breakdown */}
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>💳 Plan Breakdown</div>
          {[
            { label: "Free",    count: data?.plans?.free || 0,    color: SUB },
            { label: "Premium", count: data?.plans?.premium || 0, color: PRI },
            { label: "Elite",   count: data?.plans?.elite || 0,   color: GOLD },
          ].map((p, i) => {
            const total = (data?.plans?.free || 0) + (data?.plans?.premium || 0) + (data?.plans?.elite || 0);
            const pct = total > 0 ? Math.round(p.count / total * 100) : 0;
            return (
              <div key={i} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 5, background: p.color }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>{p.label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 800, color: p.color }}>{p.count} ({pct}%)</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: BORDER }}>
                  <div style={{ height: 6, borderRadius: 3, background: p.color, width: `${pct}%`, transition: "width 0.8s" }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Verification stats */}
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>🛡️ Verification Stats</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {[
            { label: "Verified",   count: data?.verification?.verified || 0,   color: GRN },
            { label: "Unverified", count: data?.verification?.unverified || 0, color: RED },
            { label: "Pending",    count: data?.verification?.pending || 0,    color: GOLD },
          ].map((v, i) => (
            <div key={i} style={{ background: `${v.color}10`, borderRadius: 12, padding: 16, textAlign: "center", border: `1px solid ${v.color}25` }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: v.color }}>{v.count}</div>
              <div style={{ fontSize: 12, color: SUB, marginTop: 6 }}>{v.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Top active users */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>🏆 Most Active Users (7 days)</div>
          {!(data?.top_active?.length)
            ? <div style={{ textAlign: "center", padding: "24px 0", color: SUB }}>No data yet</div>
            : data.top_active.slice(0, 10).map((u, i) => (
              <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < 9 ? `1px solid ${BORDER}` : "none" }}>
                <span style={{ fontSize: 11, color: SUB, width: 20, fontWeight: 700 }}>{i + 1}</span>
                <div style={{ width: 30, height: 30, borderRadius: 15, background: `linear-gradient(135deg, ${PRI}, #a23ad1)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{u.avatar_emoji || "👤"}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: TEXT }}>{u.name}</div>
                  <div style={{ fontSize: 10, color: SUB }}>{u.posts_count} posts · {u.dm_count} DMs</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: GOLD }}>★{u.trust_score}</div>
              </div>
            ))
          }
        </div>

        {/* Top messagers */}
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>💬 Top DM Users (30 days)</div>
          {!(data?.top_messagers?.length)
            ? <div style={{ textAlign: "center", padding: "24px 0", color: SUB }}>No data yet</div>
            : data.top_messagers.map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < data.top_messagers.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                <span style={{ fontSize: 11, color: SUB, width: 20, fontWeight: 700 }}>{i + 1}</span>
                <div style={{ width: 30, height: 30, borderRadius: 15, background: `linear-gradient(135deg, #06b6d4, ${PRI})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{item.user?.avatar_emoji || "👤"}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: TEXT }}>{item.user?.name || "Unknown"}</div>
                  <div style={{ fontSize: 10, color: SUB, textTransform: "capitalize" }}>{item.user?.plan || "free"}</div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#06b6d4" }}>{item.msg_count}</div>
                <span style={{ fontSize: 10, color: SUB }}>msgs</span>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}